import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import SearchableSelect from "../../components/common/SearchableSelect";
import { errorAlert, successAlert, warningAlert } from "../../utils/alert";

const AGE_OPTIONS = [
  { value: "all", label: "All Age Categories" },
  { value: "sub_junior", label: "Sub-Junior" },
  { value: "junior", label: "Junior" },
  { value: "senior", label: "Senior" },
];

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const normalizeGender = (value) => {
  const v = String(value || "").toLowerCase().trim();
  if (v === "male" || v === "men") return "male";
  if (v === "female" || v === "women") return "female";
  return "";
};

const normalizeAgeCategory = (value) => {
  const v = String(value || "").toLowerCase().trim();
  if (v === "all") return "all";
  if (v === "sub_junior" || v === "sub-junior" || v === "sub junior") return "sub_junior";
  if (v === "junior") return "junior";
  if (v === "senior") return "senior";
  return "";
};

const createEmptyRule = () => ({
  id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  gender: "male",
  age_category: "sub_junior",
  sport_id: "",
  game_id: "",
  sport: "",
  event: "",
  competition_class: "",
  eligible_classes: "",
  mqs: "",
  equipment: "-",
});

const splitClasses = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const ClassificationMultiSelect = ({ options = [], value, onChange, disabled = false }) => {
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const selected = splitClasses(value);
  const available = Array.from(new Set(options.map((item) => String(item || "").trim()).filter(Boolean)));
  const displayOptions = Array.from(new Set([...available, ...selected]));
  const selectedSet = new Set(selected);
  const allSelected = available.length > 0 && available.every((item) => selectedSet.has(item));

  const toggle = (classification) => {
    const next = selectedSet.has(classification)
      ? selected.filter((item) => item !== classification)
      : [...selected, classification];
    onChange(next.join(","));
  };

  useEffect(() => {
    if (!open) return undefined;
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      const menuHeight = 288;
      const spaceBelow = window.innerHeight - rect.bottom;
      const opensAbove = spaceBelow < menuHeight && rect.top > spaceBelow;
      setMenuStyle({
        position: "fixed",
        left: rect.left,
        top: opensAbove ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4,
        width: Math.max(320, rect.width),
        maxWidth: "80vw",
        zIndex: 9999,
      });
    }

    const closeOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const closeOnScroll = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    window.addEventListener("scroll", closeOnScroll, true);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      window.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={`w-full min-h-[34px] border border-gray-300 rounded px-2 py-1 cursor-pointer bg-white flex items-center justify-between gap-2 ${
          disabled ? "opacity-60 cursor-not-allowed bg-gray-100" : ""
        }`}
        onClick={() => !disabled && setOpen((current) => !current)}
        disabled={disabled}
      >
        <span className="truncate text-xs" title={selected.join(", ")}>
          {disabled
            ? "Select a sport first"
            : selected.length
              ? selected.join(", ")
              : "Select eligible classes"}
        </span>
        <span aria-hidden="true">▾</span>
      </button>

      {!disabled && open && menuStyle && createPortal(
        <div ref={menuRef} style={menuStyle} className="rounded-lg border border-gray-200 bg-white shadow-xl p-2">
          <div className="flex items-center justify-between gap-2 pb-2 mb-2 border-b">
            <button
              type="button"
              onClick={() => onChange(allSelected ? "" : available.join(","))}
              disabled={!available.length}
              className="text-xs font-semibold text-blue-700 hover:underline disabled:text-gray-400"
            >
              {allSelected ? "Clear All" : `Select All (${available.length})`}
            </button>
            {selected.length > 0 && (
              <button type="button" onClick={() => onChange("")} className="text-xs text-gray-600 hover:underline">
                Clear
              </button>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1">
            {!displayOptions.length ? (
              <p className="p-2 text-xs text-amber-700">No classifications found for this sport.</p>
            ) : (
              displayOptions.map((classification) => (
                <label
                  key={classification}
                  className={`flex items-center gap-2 rounded px-2 py-1.5 text-xs cursor-pointer ${
                    selectedSet.has(classification) ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSet.has(classification)}
                    onChange={() => toggle(classification)}
                  />
                  <span>{classification}</span>
                  {!available.includes(classification) && (
                    <span className="ml-auto text-[10px] text-amber-700">Legacy value</span>
                  )}
                </label>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const EligibilityManager = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState("database");
  const [query, setQuery] = useState("");
  const [genderFilter, setGenderFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [showOnlyInvalid, setShowOnlyInvalid] = useState(false);
  const [sports, setSports] = useState([]);
  const [classificationsBySport, setClassificationsBySport] = useState({});
  const [gamesBySport, setGamesBySport] = useState({});

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await API.fetchEligibilityRules();
      const list = Array.isArray(res?.data) ? res.data : [];
      setRules(
        list.map((r, i) => ({
          id: r?.id ?? `tmp_loaded_${i}`,
          gender: normalizeGender(r?.gender) || "male",
          age_category: normalizeAgeCategory(r?.age_category) || "sub_junior",
          sport_id: String(r?.sport_id || ""),
          game_id: String(r?.game_id || ""),
          sport: String(r?.sport || ""),
          event: String(r?.event || ""),
          competition_class: String(r?.competition_class || ""),
          eligible_classes: String(r?.eligible_classes || ""),
          mqs: String(r?.mqs || ""),
          equipment: String(r?.equipment || "-"),
        }))
      );
      setSource(String(res?.source || "database"));
    } catch (err) {
      errorAlert("Load failed", err?.message || "Unable to load eligibility rules.");
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  useEffect(() => {
    const loadEligibilityOptions = async () => {
      try {
        const sportRows = await API.getSports();
        const list = (Array.isArray(sportRows) ? sportRows : sportRows?.data || [])
          .filter((sport) => sport?.id && String(sport?.name || "").trim())
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setSports(list);

        const results = await Promise.all(
          list.map(async (sport) => {
            const [classificationResult, gamesResult] = await Promise.allSettled([
              API.get(
                `/admin/classifications/sports/${sport.id}/classifications`
              ),
              API.fetchGamesBySport(sport.id),
            ]);
            const response = classificationResult.status === "fulfilled" ? classificationResult.value : [];
            const rows = Array.isArray(response) ? response : response?.data || [];
            const games = gamesResult.status === "fulfilled" && Array.isArray(gamesResult.value)
              ? gamesResult.value
              : [];
            return {
              id: String(sport.id),
              classifications: Array.from(
                new Set(rows.map((row) => String(row?.classification || "").trim()).filter(Boolean))
              ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
              games: games
                .filter((game) => game?.id && String(game?.game_name || "").trim())
                .sort((a, b) => String(a.game_name).localeCompare(String(b.game_name))),
            };
          })
        );
        setClassificationsBySport(Object.fromEntries(results.map((result) => [result.id, result.classifications])));
        setGamesBySport(Object.fromEntries(results.map((result) => [result.id, result.games])));
      } catch (err) {
        errorAlert("Options unavailable", err?.message || "Unable to load sports and classifications.");
        setSports([]);
        setClassificationsBySport({});
        setGamesBySport({});
      }
    };

    loadEligibilityOptions();
  }, []);

  const sportNameForId = (sportId) =>
    String(sports.find((sport) => String(sport.id) === String(sportId))?.name || "");

  const classificationOptionsForSport = (sportId) => {
    const base = sportId ? classificationsBySport[String(sportId)] || [] : [];
    const sportName = sportNameForId(sportId);
    const normalizedSport = String(sportName || "").trim().toLowerCase();
    if (normalizedSport === "para athletics") {
      return Array.from(
        new Set([...base, ...base.filter((item) => /^T\d/i.test(item)).map((item) => `F${item.slice(1)}`)])
      ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    if (normalizedSport === "para swimming") {
      const numbers = base
        .map((item) => String(item).match(/^S(\d+)$/i)?.[1])
        .filter(Boolean);
      return Array.from(new Set([...base, ...numbers.map((n) => `SB${n}`), ...numbers.map((n) => `SM${n}`)]))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    return base;
  };

  const gameOptionsForSport = (sportId) => {
    return sportId ? gamesBySport[String(sportId)] || [] : [];
  };

  const validateRow = (row) =>
    !normalizeGender(row.gender) ||
    !normalizeAgeCategory(row.age_category) ||
    !String(row.sport_id || "").trim() ||
    !String(row.game_id || "").trim() ||
    !String(row.competition_class || "").trim() ||
    !String(row.eligible_classes || "").trim() ||
    !String(row.mqs || "").trim();

  const rowsWithIdx = useMemo(
    () => rules.map((r, i) => ({ ...r, _idx: i, _invalid: validateRow(r) })),
    [rules]
  );

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rowsWithIdx.filter((row) => {
      if (genderFilter !== "all" && normalizeGender(row.gender) !== genderFilter) return false;
      if (ageFilter !== "all" && normalizeAgeCategory(row.age_category) !== ageFilter) return false;
      if (showOnlyInvalid && !row._invalid) return false;
      if (!q) return true;
      const hay = `${row.sport} ${row.event} ${row.competition_class} ${row.eligible_classes} ${row.mqs} ${row.equipment}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rowsWithIdx, query, genderFilter, ageFilter, showOnlyInvalid]);

  const invalidCount = useMemo(() => rowsWithIdx.filter((r) => r._invalid).length, [rowsWithIdx]);

  const updateRule = (idx, field, value) => {
    setRules((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const updateRuleFields = (idx, fields) => {
    setRules((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...fields };
      return copy;
    });
  };

  const deleteRule = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const addRule = () => {
    const newRule = createEmptyRule();

    // Put new rules where the user can see them immediately. Previously they
    // were appended after the complete ruleset and could also be hidden by an
    // active search/filter, which made the button appear to do nothing.
    setQuery("");
    setGenderFilter("all");
    setAgeFilter("all");
    setShowOnlyInvalid(false);
    setRules((prev) => [newRule, ...prev]);

    window.setTimeout(() => {
      const input = document.getElementById(`eligibility-sport-${newRule.id}__search`);
      input?.scrollIntoView({ behavior: "smooth", block: "center" });
      input?.focus();
    }, 0);
  };

  const normalizeForSave = (row) => ({
    gender: normalizeGender(row.gender),
    age_category: normalizeAgeCategory(row.age_category),
    sport_id: Number(row.sport_id),
    game_id: Number(row.game_id),
    competition_class: String(row.competition_class || "").trim().toUpperCase(),
    eligible_classes: String(row.eligible_classes || "").trim(),
    mqs: String(row.mqs || "").trim(),
    equipment: String(row.equipment || "-").trim() || "-",
  });

  const saveRules = async () => {
    if (!rules.length) {
      warningAlert("No rows", "Add at least one rule before saving.");
      return;
    }

    const normalized = rules.map(normalizeForSave);
    const missingIdx = normalized.findIndex(
      (r) => !r.gender || !r.age_category || !r.sport_id || !r.game_id || !r.competition_class || !r.eligible_classes || !r.mqs
    );
    if (missingIdx >= 0) {
      warningAlert("Validation", `Row ${missingIdx + 1} has missing required fields.`);
      return;
    }

    const seen = new Set();
    for (let i = 0; i < normalized.length; i += 1) {
      const r = normalized[i];
      const key = `${r.gender}|${r.age_category}|${r.sport_id}|${r.game_id}|${r.competition_class.toLowerCase()}|${r.eligible_classes.toLowerCase()}`;
      if (seen.has(key)) {
        warningAlert(
          "Validation",
          `Duplicate rule at row ${i + 1} with the same gender, age, sport, event, competition class, and eligible classes.`
        );
        return;
      }
      seen.add(key);
    }

    setSaving(true);
    try {
      const res = await API.saveEligibilityRules(normalized);
      successAlert("Saved", res?.message || "Eligibility rules updated.");
      await loadRules();
    } catch (err) {
      errorAlert("Save failed", err?.message || "Unable to save eligibility rules.");
    } finally {
      setSaving(false);
    }
  };

  const resetFromConfig = async () => {
    try {
      const res = await API.resetEligibilityRules();
      successAlert("Reloaded", res?.message || "Rules reloaded from config.");
      await loadRules();
    } catch (err) {
      errorAlert("Reset failed", err?.message || "Unable to reload from config.");
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Eligibility Management</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage game eligibility by gender, age category, sport, and event.
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Source: {source === "database" ? "Database" : "Config fallback"} | Total: {rules.length} | Invalid:{" "}
                {invalidCount}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate("/admin")}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm"
              >
                Back to Dashboard
              </button>
              <button
                onClick={resetFromConfig}
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm"
              >
                Reload From Config
              </button>
              <button
                type="button"
                onClick={addRule}
                disabled={loading || saving}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Add Rule
              </button>
              <button
                onClick={saveRules}
                disabled={saving || loading}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:bg-gray-400"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-4 mb-4">
            <div className="grid md:grid-cols-5 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Search</label>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Sport / event / classes / MQS"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Gender</label>
                <SearchableSelect
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">All</option>
                  {GENDER_OPTIONS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Age Category</label>
                <SearchableSelect
                  value={ageFilter}
                  onChange={(e) => setAgeFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">All</option>
                  {AGE_OPTIONS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showOnlyInvalid}
                    onChange={(e) => setShowOnlyInvalid(e.target.checked)}
                  />
                  Show only invalid rows
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 border">#</th>
                    <th className="p-2 border">Gender</th>
                    <th className="p-2 border">Age Category</th>
                    <th className="p-2 border">Sport</th>
                    <th className="p-2 border">Event</th>
                    <th className="p-2 border">Competition Class</th>
                    <th className="p-2 border">Eligible Classes</th>
                    <th className="p-2 border">MQS</th>
                    <th className="p-2 border">Equipment</th>
                    <th className="p-2 border">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && !filteredRows.length ? (
                    <tr>
                      <td className="p-3 text-center text-gray-500 border" colSpan={10}>
                        No rules found for current filters.
                      </td>
                    </tr>
                  ) : null}
                  {filteredRows.map((row, i) => (
                    <tr key={`${row.id}_${row._idx}`} className={row._invalid ? "bg-rose-50" : ""}>
                      <td className="p-2 border align-top">{i + 1}</td>
                      <td className="p-2 border align-top min-w-[120px]">
                        <SearchableSelect
                          value={row.gender}
                          onChange={(e) => updateRule(row._idx, "gender", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        >
                          {GENDER_OPTIONS.map((g) => (
                            <option key={g.value} value={g.value}>
                              {g.label}
                            </option>
                          ))}
                        </SearchableSelect>
                      </td>
                      <td className="p-2 border align-top min-w-[140px]">
                        <SearchableSelect
                          value={row.age_category}
                          onChange={(e) => updateRule(row._idx, "age_category", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        >
                          {AGE_OPTIONS.map((a) => (
                            <option key={a.value} value={a.value}>
                              {a.label}
                            </option>
                          ))}
                        </SearchableSelect>
                      </td>
                      <td className="p-2 border align-top min-w-[180px]">
                        <SearchableSelect
                          id={`eligibility-sport-${row.id}`}
                          value={row.sport_id}
                          onChange={(e) => {
                            updateRuleFields(row._idx, {
                              sport_id: String(e.target.value || ""),
                              game_id: "",
                              sport: sportNameForId(e.target.value),
                              event: "",
                              competition_class: "",
                              eligible_classes: "",
                            });
                          }}
                          placeholder="Select sport"
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="">Select sport</option>
                          {sports.map((sport) => (
                            <option key={sport.id} value={sport.id}>
                              {sport.name}
                            </option>
                          ))}
                        </SearchableSelect>
                      </td>
                      <td className="p-2 border align-top min-w-[180px]">
                        <SearchableSelect
                          value={row.game_id}
                          onChange={(e) => {
                            const game = gameOptionsForSport(row.sport_id)
                              .find((item) => String(item.id) === String(e.target.value));
                            updateRuleFields(row._idx, {
                              game_id: String(e.target.value || ""),
                              event: String(game?.game_name || ""),
                              competition_class: "",
                            });
                          }}
                          disabled={!row.sport_id}
                          placeholder={row.sport_id ? "Select event" : "Select sport first"}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="">Select event</option>
                          {gameOptionsForSport(row.sport_id).map((game) => (
                            <option key={game.id} value={game.id}>
                              {game.game_name}
                            </option>
                          ))}
                        </SearchableSelect>
                      </td>
                      <td className="p-2 border align-top min-w-[150px]">
                        <input
                          type="text"
                          value={row.competition_class}
                          onChange={(e) => updateRule(row._idx, "competition_class", e.target.value.toUpperCase())}
                          placeholder="e.g. T47"
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="p-2 border align-top min-w-[260px]">
                        <ClassificationMultiSelect
                          options={classificationOptionsForSport(row.sport_id)}
                          value={row.eligible_classes}
                          onChange={(value) => updateRule(row._idx, "eligible_classes", value)}
                          disabled={!row.sport_id}
                        />
                      </td>
                      <td className="p-2 border align-top min-w-[130px]">
                        <input
                          type="text"
                          value={row.mqs}
                          onChange={(e) => updateRule(row._idx, "mqs", e.target.value)}
                          placeholder="e.g. 00:14.50"
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="p-2 border align-top min-w-[120px]">
                        <input
                          type="text"
                          value={row.equipment}
                          onChange={(e) => updateRule(row._idx, "equipment", e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      </td>
                      <td className="p-2 border align-top min-w-[90px]">
                        <button
                          onClick={() => deleteRule(row._idx)}
                          className="px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700 text-xs"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EligibilityManager;
